import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import desktopAgentIcon from '@easytools-assets/desktop-agent/desktop-agent.svg';
import { DESKTOP_AGENT_STATE_LABEL, useDesktopAgent } from '../../contexts/DesktopAgentContext';

const HINTS = {
    checking: 'Sto cercando il Desktop Agent su questo PC.',
    offline: 'Avvia il Desktop Agent su questo PC, oppure installalo dalla pagina dedicata.',
    limited: 'Installa e autentica Claude Code o Codex: servono per generare contenuti in sola lettura.',
    ready: 'Puoi generare e tradurre contenuti dei tool. Il Desktop Agent legge il codice, non lo modifica.',
} as const;

/**
 * Icona del Desktop Agent nella barra in alto, accanto all'utente. Colorata quando l'agent
 * è pronto, grigia quando non risponde; lo stato è sempre anche scritto (etichetta e riquadro).
 */
const DesktopAgentIndicator: React.FC = () => {
    const agent = useDesktopAgent();
    const [open, setOpen] = useState(false);
    const root = useRef<HTMLDivElement>(null);
    const label = DESKTOP_AGENT_STATE_LABEL[agent.state];

    useEffect(() => {
        if (!open) return;
        const close = (e: MouseEvent | KeyboardEvent) => {
            if (e instanceof KeyboardEvent ? e.key === 'Escape' : !root.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', close);
        document.addEventListener('keydown', close);
        return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', close); };
    }, [open]);

    return (
        <div className="agent-indicator" ref={root}>
            <button type="button" className={`agent-indicator__btn is-${agent.state}`} aria-expanded={open} aria-haspopup="dialog"
                aria-label={`Desktop Agent: ${label}`} title={`Desktop Agent: ${label}`} onClick={() => setOpen(o => !o)}>
                <img src={desktopAgentIcon} alt="" aria-hidden="true" />
                <span className="agent-indicator__dot" aria-hidden="true" />
            </button>
            {open && (
                <div className="agent-indicator__panel" role="dialog" aria-label="Desktop Agent">
                    <div className="agent-indicator__head">
                        <img src={desktopAgentIcon} alt="" aria-hidden="true" />
                        <div>
                            <strong>Desktop Agent</strong>
                            <span className={`agent-indicator__state is-${agent.state}`}>{label}{agent.health?.version ? ` · versione ${agent.health.version}` : ''}</span>
                        </div>
                    </div>
                    <p className="agent-indicator__hint">{HINTS[agent.state]}</p>
                    {agent.health && (
                        <ul className="agent-indicator__clis">
                            {agent.health.agents.map(cli => (
                                <li key={cli.code}>
                                    <span>{cli.displayName}</span>
                                    <span className={cli.available && cli.supportsReadOnly ? 'is-ok' : 'is-off'}>
                                        {!cli.available ? 'non installata' : cli.supportsReadOnly ? (agent.agent === cli.code ? 'in uso' : 'disponibile') : 'senza sola lettura'}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {agent.readOnlyAgents.length > 1 && (
                        <label className="agent-indicator__choose">
                            CLI da usare
                            <select className="form-select form-select-sm" value={agent.agent ?? ''} onChange={e => agent.setAgent(e.target.value)}>
                                {agent.readOnlyAgents.map(cli => <option key={cli.code} value={cli.code}>{cli.displayName}</option>)}
                            </select>
                        </label>
                    )}
                    <div className="agent-indicator__actions">
                        <button type="button" className="btn btn-sm btn-light" onClick={() => void agent.refresh()} disabled={agent.state === 'checking'}>
                            Verifica di nuovo
                        </button>
                        <Link to="/desktop-agent" className="btn btn-sm btn-outline-primary" onClick={() => setOpen(false)}>Pagina Desktop Agent</Link>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DesktopAgentIndicator;
